import type { Contact, ImageItem, Live, SiteContent, Work } from './types';

// In dev, CRA's proxy (package.json "proxy") forwards /api to the server.
// In prod, the same Express server serves the frontend, so /api is relative.
const BASE = process.env.REACT_APP_API_URL ?? '';

async function request<T>(
    path: string,
    init: RequestInit = {},
): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
        credentials: 'include',
        headers: {
            ...(init.body && !(init.body instanceof FormData)
                ? { 'Content-Type': 'application/json' }
                : {}),
            ...(init.headers ?? {}),
        },
        ...init,
    });
    if (!res.ok) {
        let message = `Request failed: ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error) message = body.error;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export const api = {
    works: {
        list: () => request<Work[]>('/api/works'),
        get: (slug: string) =>
            request<Work>(`/api/works/${encodeURIComponent(slug)}`),
        create: (data: Partial<Work>) =>
            request<Work>('/api/works', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        update: (id: number, data: Partial<Work>) =>
            request<Work>(`/api/works/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            }),
        remove: (id: number) =>
            request<{ ok: true }>(`/api/works/${id}`, { method: 'DELETE' }),
        reorder: (items: Array<{ id: number; sortOrder: number }>) =>
            request<{ ok: true }>('/api/works/reorder', {
                method: 'POST',
                body: JSON.stringify(items),
            }),
    },
    lives: {
        list: () => request<Live[]>('/api/lives'),
        create: (data: Partial<Live>) =>
            request<Live>('/api/lives', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        update: (id: number, data: Partial<Live>) =>
            request<Live>(`/api/lives/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            }),
        remove: (id: number) =>
            request<{ ok: true }>(`/api/lives/${id}`, { method: 'DELETE' }),
    },
    content: {
        get: (key: string) =>
            request<SiteContent>(`/api/content/${encodeURIComponent(key)}`),
        set: (key: string, value: string) =>
            request<SiteContent>(`/api/content/${encodeURIComponent(key)}`, {
                method: 'PUT',
                body: JSON.stringify({ value }),
            }),
    },
    contacts: {
        list: () => request<Contact[]>('/api/contacts'),
        create: (data: Partial<Contact>) =>
            request<Contact>('/api/contacts', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        update: (id: number, data: Partial<Contact>) =>
            request<Contact>(`/api/contacts/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            }),
        remove: (id: number) =>
            request<{ ok: true }>(`/api/contacts/${id}`, { method: 'DELETE' }),
    },
    images: {
        list: () => request<ImageItem[]>('/api/images'),
        upload: (file: File) => {
            const fd = new FormData();
            fd.append('file', file);
            return request<{ filename: string; url: string }>('/api/images', {
                method: 'POST',
                body: fd,
            });
        },
        remove: (filename: string) =>
            request<{ ok: true }>(
                `/api/images/${encodeURIComponent(filename)}`,
                { method: 'DELETE' },
            ),
    },
    auth: {
        login: (password: string) =>
            request<{ ok: true }>('/api/auth/login', {
                method: 'POST',
                body: JSON.stringify({ password }),
            }),
        logout: () =>
            request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
        me: () => request<{ authenticated: boolean }>('/api/auth/me'),
    },
};
